import React from 'react';
import { Cell, Pie, PieChart, Tooltip, Sector } from 'recharts';
import PieLabel from '../PieLabel';
import generateKey from '../../../../utils/methods/generateKey';
import generateComparisionColors from '../../methods/generateComparisionColors';
import TooltipContent from '../TooltipContent';
import PieLine from '../PieLine';
import { startAngle, endAngle } from '../../pieHelpers';
import { pieSegmentColors } from '../../pieColors';
import generatePieColors from '../../methods/generatePieColors';

const PieChartContent = ({
  data,
  labelSegment,
  pieChartUnit,
  pieChartLabelThreshold,
  total,
  setMargins,
  leftMargin,
  rightMargin,
  t,
  showWhiteSpace,
  hideTooltip,
  syncId,
  activeIndex,
  onPieEnter,
  onPieLeave,
  onAnimationEnd,
  animationEndedKey,
  disableAnimations,
} = {}) => {
  const compareColors = labelSegment !== null
    ? generateComparisionColors(
      {
        number: data.length,
        index: labelSegment.id,
      },
      pieSegmentColors,
    )
    : generatePieColors(data.length).map(({ value }) => value);

  const key = Math.random();

  return (
    <PieChart
      margin={{
        top: 0,
        right: rightMargin,
        bottom: 0,
        left: leftMargin,
      }}
      syncId={syncId ? `${syncId}_${data.length}` : undefined}
      key={animationEndedKey || key}
      // Workaround to an old bug in recharts that is still not fixed (November 2023)
      // example thread (there is a lot of them): https://github.com/recharts/recharts/issues/1083
    >
      {!hideTooltip && (
        <Tooltip
          content={p => TooltipContent({
            ...p,
            chartType: 'pie',
            forceUnit: pieChartUnit,
            total,
            t,
            showWhiteSpace,
          })}
        />
      )}
      <Pie
        data={data}
        labelLine={props => <PieLine {...props} threshold={pieChartLabelThreshold} />}
        label={
          props => (
            <PieLabel {...props} threshold={pieChartLabelThreshold} setMargins={setMargins} />
          )
        }
        dataKey="value"
        startAngle={startAngle}
        endAngle={endAngle}
        activeShape={({ cx, cy, innerRadius, outerRadius, startAngle: sa, endAngle: ea, fill }) => (
          <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius + 8}
            startAngle={sa}
            endAngle={ea}
            fill={fill}
          />
        )}
        onMouseEnter={onPieEnter}
        onMouseLeave={onPieLeave}
        activeIndex={activeIndex}
        isAnimationActive={!disableAnimations}
        onAnimationEnd={() => !animationEndedKey && onAnimationEnd(key)}
      >
        {
          data.map((entry, index) => {
            const color = entry.color
              ? generatePieColors(data.length).find(item => item.name === entry.color).value
              : compareColors[index % compareColors.length];

            return (
              <Cell
                fill={color}
                key={generateKey(index, color)}
              />
            );
          })
        }
      </Pie>
    </PieChart>
  );
};

PieChartContent.defaultProps = {
  labelSegment: null,
  pieChartUnit: null,
};

export default PieChartContent;
